import React, { useState, useEffect, useRef } from 'react';
import './PaymentHistory.css';
import profileIcon from '../../shared/assets/icons/profile.png';
import { useNavigate } from 'react-router-dom';
import HeaderNav from '../../shared/components/HeaderNav';
import MobileFooterNav from '../../shared/components/MobileFooterNav';
import MainLoader from '../../shared/components/MainLoader';
import DataTableGrid from '../../shared/components/DataTableGrid';
import { IoArrowUndoOutline } from 'react-icons/io5';
import { LuFilter } from 'react-icons/lu';
import { paymentService } from '../invoice/services/paymentService';
import { getOrgGuid, getCustomerId } from '../../shared/utils/authStorage';
import { formatCurrency, formatDateTime } from '../../shared/utils/localeHelper';

const STATUS_LABELS = {
    paid: 'Paid',
    failed: 'Failed',

};

const STATUS_COLORS = {
    paid: '#008b25',
    failed: '#e53935',
    pending_verification: '#e4be00',
    created: '#004eba',
    refunded: '#7b1fa2',
};

const PaymentHistory = () => {
    const navigate = useNavigate();
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [totalItems, setTotalItems] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortField, setSortField] = useState('-updated_at');
    const [filterStatus, setFilterStatus] = useState('');
    const [showFilterPanel, setShowFilterPanel] = useState(false);
    const itemsPerPage = 10;
    const isMobile = window.innerWidth <= 768;
    const filterRef = useRef(null);

    const orgGuid = getOrgGuid();
    const customerId = getCustomerId();

    const fetchTransactions = async (page_number = 1) => {
        if (!orgGuid) return;
        setLoading(true);
        setError(null);
        try {
            const params = {
                'filter[organizational_unit_guid]': orgGuid,
                page_number,
                'per-page': itemsPerPage,
                sort: sortField,

            };
            if (customerId) {
                params['filter[customer_id]'] = customerId;
            }
            if (filterStatus) params['filter[payment_status]'] = filterStatus;

            const res = await paymentService.listTransactions(params);
            const data = Array.isArray(res.data) ? res.data : (res.data?.data || []);
            setTransactions(data);
            const total = parseInt(res.headers?.['x-pagination-total-count'] || data.length, 10);
            setTotalItems(total);
        } catch (err) {
            setError('Failed to load payment history.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchTransactions(currentPage); }, [currentPage, sortField, filterStatus]);

    useEffect(() => {
        if (!showFilterPanel) return;
        const handler = (e) => { if (filterRef.current && !filterRef.current.contains(e.target)) setShowFilterPanel(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [showFilterPanel]);

    const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));

    const handleSort = (field) => {
        setSortField(prev => prev === field ? `-${field}` : field);
        setCurrentPage(1);
    };

    const getSortIcon = (field) => {
        if (sortField === field) return ' ↑';
        if (sortField === `-${field}`) return ' ↓';
        return '';
    };

    const statusBadge = (status) => (
        <span className="ph-status-badge" style={{ background: `${STATUS_COLORS[status] || '#888'}22`, color: STATUS_COLORS[status] || '#888' }}>
            {STATUS_LABELS[status] || status}
        </span>
    );

    const columns = [
        { key: 'payment_trans_number', title: 'Transactions', sortKey: 'created_at' },
        { key: 'payment_method', title: 'Method', sortKey: 'payment_method' },
        { key: 'payment_type', title: 'Type' },
        { key: 'amount', title: 'Amount', sortKey: 'amount', render: (row) => formatCurrency(row.amount) },
        { key: 'payment_status', title: 'Status', render: (row) => statusBadge(row.payment_status) },
    ];

    return (
        <div className="ph-container">
            <HeaderNav profileIcon={profileIcon} />

            {/* Mobile header */}
            <header className="ph-mobile-header">
                <div onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer' }}>
                    <IoArrowUndoOutline color="black" size={22} />
                </div>
                <h2 className="ph-mobile-title">Payment History</h2>
                <div style={{ width: 22 }}></div>
            </header>

            {/* Desktop header */}
            <div className="ph-desktop-header">
                <h2 className="ph-page-title">Payment History</h2>
                {/* <div style={{ position: 'relative', marginRight: 20 }} ref={filterRef}>
                    <button className="ph-filter-btn" onClick={() => setShowFilterPanel(p => !p)}>
                        <LuFilter size={15} color="white" />
                        {filterStatus && <span className="ph-filter-dot" />}
                    </button>
                    {showFilterPanel && <FilterPanel filterStatus={filterStatus} setFilterStatus={(v) => { setFilterStatus(v); setCurrentPage(1); setShowFilterPanel(false); }} />}
                </div> */}
            </div>

            {/* Desktop table */}
            <div className="ph-table-container" style={{ display: isMobile ? 'none' : undefined }}>
                {loading ? <MainLoader /> : transactions.length === 0 ? (
                    <div className="ph-empty"><span>💳</span><p>{error || 'No payment history found.'}</p></div>
                ) : (
                    <DataTableGrid
                        items={transactions}
                        loading={false}
                        error={null}
                        columns={columns}
                        onSort={handleSort}
                        sortField={sortField}
                        getSortIcon={getSortIcon}
                    />
                )}
            </div>

            {/* Desktop pagination */}
            {!isMobile && totalPages > 1 && (
                <div className="ph-pagination">
                    <button className="ph-page-btn" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1 || loading}>{'<'}</button>
                    {[...Array(totalPages)].map((_, i) => (
                        <button key={i} className={`ph-page-btn ${currentPage === i + 1 ? 'active' : ''}`} onClick={() => setCurrentPage(i + 1)} disabled={loading}>{i + 1}</button>
                    ))}
                    <button className="ph-page-btn" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages || loading}>{'>'}</button>
                </div>
            )}

            {/* Mobile cards */}
            <div className="ph-mobile-list" style={{ display: isMobile ? 'flex' : 'none' }}>
                {loading ? <MainLoader /> : transactions.length === 0 ? (
                    <div className="ph-empty"><span>💳</span><p>{error || 'No payment history found.'}</p></div>
                ) : (
                    transactions.map((tx, i) => (
                        <div key={tx.guid || i} className="ph-card">
                            <div className="ph-card-row">
                                <span className="ph-card-trans">{tx.payment_trans_number}</span>
                                {statusBadge(tx.payment_status)}
                            </div>
                            <div className="ph-card-row">
                                <span className="ph-card-label">{tx.payment_method}</span>
                                <span className="ph-card-amount">{formatCurrency(tx.amount)}</span>
                            </div>
                            {tx.paid_date && <div className="ph-card-date">{formatDateTime(tx.paid_date)}</div>}
                        </div>
                    ))
                )}
                {/* Mobile pagination */}
                {totalPages > 1 && (
                    <div className="ph-pagination">
                        <button className="ph-page-btn" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1 || loading}>{'<'}</button>
                        <span className="ph-page-info">{currentPage} / {totalPages}</span>
                        <button className="ph-page-btn" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages || loading}>{'>'}</button>
                    </div>
                )}
            </div>

            <MobileFooterNav />
        </div>
    );
};

const FilterPanel = ({ filterStatus, setFilterStatus }) => (
    <div className="ph-filter-panel">
        <p className="ph-filter-heading">Filter by Status</p>
        {['', 'paid', 'failed', 'pending_verification', 'created', 'refunded'].map(s => (
            <button key={s} className={`ph-filter-option ${filterStatus === s ? 'active' : ''}`} onClick={() => setFilterStatus(s)}>
                {s ? STATUS_LABELS[s] : 'All'}
            </button>
        ))}
    </div>
);

export default PaymentHistory;
